import type { LoggerFactory } from '@amp/web-apps-logger';
import { ERROR_SIGIL } from './constants';
import type { PageSpeedContext, IPerfkit, PageData } from './types';

const CONTEXT_NAME = 'Perfkit-context';

export function setPageSpeedContext(
    context: Map<string, unknown>,
    perfKit: IPerfkit,
    logger: LoggerFactory,
): void {
    const log = logger.loggerFor('PageSpeedContext');
    context.set(CONTEXT_NAME, {
        getPageSpeedMetric: perfKit.getPageSpeedMetric.bind(perfKit),
        /**
         * Null-safe capturePageAppearTime
         *
         * See {@link IPageSpeedMetric.capturePageAppearTime}
         * for details
         *
         */
        capturePageAppearTime: () => {
            const metric = perfKit.getPageSpeedMetric.apply(perfKit);
            if (metric !== null) {
                metric.capturePageAppearTime.apply(metric);
            } else {
                log.debug('unable to capture appear time, no metric exists.');
            }
        },
        /**
         * Null-safe capturePageData that also captures endtime
         *
         */
        capturePageData: (pageData: Partial<PageData>) => {
            const metric = perfKit.getPageSpeedMetric.apply(perfKit);
            if (metric !== null) {
                const pageMetric: PageData = {
                    pageContext: pageData?.pageContext || ERROR_SIGIL,
                    pageId: pageData?.pageId || ERROR_SIGIL,
                    pageType: pageData?.pageType || ERROR_SIGIL,
                    isFirstPage: pageData?.isFirstPage || false,
                };
                metric.capturePageData.apply(metric, [
                    pageMetric,
                    { capturePageEndTime: true },
                ]);
            } else {
                log.debug('unable to capture pageData, no metric exists.');
            }
        },
    });
}

export function generateBrowserPageSpeedContextGetter(
    getContext: (context: string) => unknown,
): () => PageSpeedContext {
    return () => getContext(CONTEXT_NAME) as PageSpeedContext;
}
